/**
 * PathwayCanvasToolbar (Sprint 4.3.5, extended Sprint 4.3.7).
 *
 * Top toolbar that sits directly above the canvas, mirroring the Awell
 * Studio reference:
 *   - left: <PathwayBreadcrumb> (Sprint 4.3.1)
 *   - right: "+ New Pathway" button (Sprint 4.3.7)
 *   - right: "Invite collaborators" button (stub; emits a click event)
 *   - right-most: Save & Publish button (Sprint 4.3.7) - blue circle
 *     "play" icon. Tooltip describes the next-publish version + the
 *     gate result (HC8 fail-closed).
 *
 * Sprint 4.3.7: the Play button is now wired to the publish flow. It
 * disables when the publish gate would fail and surfaces the gate
 * reason via tooltip + aria-describedby.
 *
 * HC6 a11y: every button has `aria-label`. HC2 token-only.
 *
 * Anchored to the canonical Scrips prototype:
 *   https://ai-native-prototype.vercel.app/care-pathway-studio.html
 *   and the Awell Studio screenshot at
 *   ~/Downloads/628df8e945c036bc4dc479bf_Home_Visual-p-1080.png
 *   (Plan v9 §4.2 prototype-anchoring rule).
 */
import {
  PathwayBreadcrumb,
  type PathwayBreadcrumbProps,
} from './PathwayBreadcrumb';

export interface PathwayCanvasToolbarProps {
  /** Breadcrumb props passed straight through. Hidden when `null`. */
  breadcrumb: PathwayBreadcrumbProps | null;
  /** Click handler for the Invite-collaborators button. */
  onInvite?: () => void;
  /**
   * Sprint 4.3.7: Save & Publish handler. Wired to
   * `PathwayPublishFlow.handlePublish` via the editor view. When
   * undefined the button stays as a non-functional stub (legacy
   * fixture compatibility).
   */
  onPlay?: () => void;
  /**
   * Sprint 4.3.7: when true the Play button disables. The toolbar reads
   * the gate result so the disable + tooltip stays in sync with the
   * publish-flow surface.
   */
  publishDisabled?: boolean;
  /** Tooltip describing why the publish is gated; hidden when ok. */
  publishDisabledReason?: string;
  /** Sprint 4.3.7: "+ New Pathway" handler. Hidden when undefined. */
  onCreatePathway?: () => void;
  /** Versioned label rendered alongside the Play icon, e.g. "v0.5". */
  publishVersionLabel?: string;
  className?: string;
}

/** Inline triangle play icon. HC2: `currentColor` only. */
function PlayIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      role="img"
      aria-hidden="true"
      className="h-3 w-3"
    >
      <path d="M7 4.5v15l13-7.5L7 4.5z" fill="currentColor" />
    </svg>
  );
}

/** Inline plus / invite icon. */
function InviteIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      role="img"
      aria-hidden="true"
      className="h-3.5 w-3.5"
    >
      <circle cx="9" cy="8" r="3.4" fill="currentColor" />
      <path
        d="M3 19c0-3 2.7-5 6-5s6 2 6 5"
        fill="currentColor"
      />
      <path
        d="M18 6v6M15 9h6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

export function PathwayCanvasToolbar({
  breadcrumb,
  onInvite,
  onPlay,
  publishDisabled,
  publishDisabledReason,
  onCreatePathway,
  publishVersionLabel,
  className,
}: PathwayCanvasToolbarProps) {
  const playLabel =
    publishVersionLabel && publishVersionLabel.length > 0
      ? `Save and publish ${publishVersionLabel}`
      : 'Save and publish pathway';
  return (
    <div
      data-testid="pathway-canvas-toolbar"
      role="toolbar"
      aria-label="Pathway canvas toolbar"
      className={[
        'flex items-center justify-between gap-gutter-md border-b border-surface-border bg-surface-base px-gutter-md py-gutter-sm',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="min-w-0 flex-1">
        {breadcrumb ? <PathwayBreadcrumb {...breadcrumb} /> : null}
      </div>
      <div className="flex items-center gap-gutter-sm">
        {onCreatePathway ? (
          <button
            type="button"
            data-testid="pathway-canvas-toolbar-new-pathway"
            aria-label="Create a new pathway"
            onClick={onCreatePathway}
            className="inline-flex items-center gap-1 rounded-signal-md border border-surface-border bg-surface-base px-gutter-md py-1 text-signal-sm font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
          >
            <span aria-hidden="true">+</span>
            <span>New Pathway</span>
          </button>
        ) : null}
        <button
          type="button"
          data-testid="pathway-canvas-toolbar-invite"
          aria-label="Invite collaborators"
          onClick={onInvite}
          className="inline-flex items-center gap-1 rounded-signal-md border border-surface-border bg-surface-base px-gutter-md py-1 text-signal-sm font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
        >
          <InviteIcon />
          <span>Invite collaborators</span>
        </button>
        <button
          type="button"
          data-testid="pathway-canvas-toolbar-play"
          aria-label={playLabel}
          aria-disabled={publishDisabled ? 'true' : 'false'}
          disabled={publishDisabled}
          title={publishDisabledReason}
          onClick={onPlay}
          data-publish-disabled={publishDisabled ? 'true' : 'false'}
          className="inline-flex items-center gap-1 rounded-full bg-signal-primary-500 px-gutter-sm py-1 text-text-inverse shadow-signal-card hover:bg-signal-primary-600 focus:outline-none focus:ring-2 focus:ring-signal-primary-300 disabled:opacity-50"
        >
          <PlayIcon />
          {publishVersionLabel ? (
            <span
              data-testid="pathway-canvas-toolbar-play-version"
              className="text-signal-xs font-semibold"
            >
              {publishVersionLabel}
            </span>
          ) : null}
        </button>
      </div>
    </div>
  );
}
